
import { every, filter, find, forEach, map, sumBy, toNumber } from 'es-toolkit/compat';
import { assertNever, filterNilValues } from './typeUtils';
import { core } from '@tock/coretypes';
import { getMinQuantity } from './option';
import { isOrderVariety } from './offerings';
import { pluralize } from './pluralize';

interface OptionInfo {
  count: number;
  ticketOptionGroupId?: number | null;
}

/**
 * Validates the requirements of the option group, with the option of ignoring the `isRequired` bit
 * so that reservationists on the dashboard side can loosen the requirements for a VIP (or w/e other reason).
 *
 * @param optionGroup The option group for which we are validating the rules
 * @param options A list of options, with their option group id and the quantity being purchased
 * @param numTicketsToSell The number of tickets being purchased
 * @param rules Configurable options for the function to follow. Allows us to not enforce isRequired
 */
export function validateSelectedOptionsForGroup(
  optionGroup: core.ITicketOptionGroup,
  options: OptionInfo[],
  numTicketsToSell: number,
  rules: {
    waiveMaximumRequirements?: boolean;
    waiveMinimumRequirements?: boolean;
  }
): boolean {
  const optionGroupType = optionGroup.optionGroupType ?? 'PER_PERSON';
  const optionsInGroup = filter(options, (option) => {
    return option.ticketOptionGroupId === optionGroup.id;
  });
  const numOptionsSelected = sumBy(optionsInGroup, 'count');
  const isRequired = !rules.waiveMinimumRequirements && optionGroup.isRequired;
  switch (optionGroupType) {
    case 'PER_PARTY': {
      const isUnderMax =
        every(optionsInGroup, (option) => option.count < 2) || !!rules.waiveMaximumRequirements;
      const isOverMin = !isRequired || numOptionsSelected > 0;
      return isUnderMax && isOverMin;
    }
    case 'UNLIMITED': {
      const isUnderMax = true;
      const isOverMin = !isRequired || numOptionsSelected > 0;
      return isUnderMax && isOverMin;
    }
    case 'PER_PERSON': {
      const isUnderMax = numOptionsSelected <= numTicketsToSell || !!rules.waiveMaximumRequirements;
      const isOverMin = !isRequired || numOptionsSelected === numTicketsToSell;
      return isUnderMax && isOverMin;
    }
    case 'FIXED_LIMIT': {
      const isUnderMax =
        numOptionsSelected <= (optionGroup.fixedLimitMax ?? 1) || !!rules.waiveMaximumRequirements;
      const isOverMin = numOptionsSelected >= (optionGroup.fixedLimitMin ?? 0) || !isRequired;
      return isOverMin && isUnderMax;
    }
    default:
      assertNever(optionGroupType);
  }
}

export function getSelectionString(
  optionOrders: core.IOptionOrder[],
  ticketOptions: core.ITicketOption[]
): string[] {
  const supplementCounts: Dictionary<number> = {};
  forEach(optionOrders, (order) => {
    supplementCounts[order.optionId as number] =
      (supplementCounts[order.optionId as number] || 0) + 1;
  });

  const selectionString = filter(
    map(supplementCounts, (quantity, optionIdString) => {
      const selectedTicketOption = find(ticketOptions, {
        id: toNumber(optionIdString),
      });

      if (selectedTicketOption) {
        return `${selectedTicketOption.name} × ${quantity}`;
      } else {
        return null;
      }
    }),
    filterNilValues
  );

  if (selectionString.length === 0) {
    selectionString.push('None added');
  }

  return selectionString;
}

interface ButtonInfo {
  text: string;
  disabled: boolean;
}

/**
 * Determines the appropriate text for the continue button if no supplements are selected (and they are not required)
 *
 * @param optionGroupEnumType The type of the option group
 * @param partySize The size of the currently booking party
 */
function getNoSupplementsSelectedButtonText(
  optionGroup: core.ITicketOptionGroup,
  partySize: number
): string {
  const optionGroupType = optionGroup.optionGroupType as core.OptionGroupType;
  switch (optionGroupType) {
    case 'PER_PARTY':
      return 'Select up to one of each add-on';
    case 'UNLIMITED':
      return 'Select as many add-ons as you would like';
    case 'PER_PERSON':
      return `Select up to ${pluralize(partySize, 'add-on')}`;
    case 'FIXED_LIMIT': {
      const min = optionGroup.fixedLimitMin ?? 0;
      const max = optionGroup.fixedLimitMax ?? 1;
      return `Select between ${min} and ${pluralize(max, 'add-on')}`;
    }
    default: {
      assertNever(optionGroupType);
    }
  }
}

/**
 * Determines the appropriate text for the continue button the groups' requirements are not met.
 *
 * @param optionGroupEnumType The type of the option group
 * @param numSelected The quantity of supplements selected for the supplement group
 * @param partySize The size of the currently booking party
 */
function getUnmetRequirementsButtonText(
  optionGroup: core.TicketOptionGroup,
  numSelected: number,
  partySize: number
): string {
  switch (optionGroup.optionGroupType) {
    case 'PER_PARTY':
    case 'UNLIMITED': {
      return 'Select at least one add-on to continue';
    }
    case 'PER_PERSON': {
      return `Select ${pluralize(partySize - numSelected, 'more add-on')} to continue`;
    }
    case 'FIXED_LIMIT': {
      const min = optionGroup.fixedLimitMin ?? 0;
      const max = optionGroup.fixedLimitMax ?? 1;
      return `Select between ${min} and ${pluralize(max, 'add-on')}`;
    }
    default: {
      assertNever(optionGroup.optionGroupType);
    }
  }
}

/**
 * VISIBLE FOR TESTING
 * Determines the button text and state for the "Continue" button at the bottom of the SupplementGroup component.
 *
 * @param optionGroup The option group being rendered
 * @param isOptionGroupInvalid Whether the current selection for the option group is valid
 * @param numSelected The quantity of supplements selected for the supplement group
 * @param partySize The party size that is booking
 * @returns The text and disabled state of the supplement button
 */
export function getButtonStateForOptionGroup(
  optionGroup: core.TicketOptionGroup,
  isOptionGroupInvalid: boolean,
  numSelected: number,
  partySize: number,
  defaultContinueText = ' Add and continue',
  disableWhenNoneSelected = true
): ButtonInfo {
  const noneSelectedText = getNoSupplementsSelectedButtonText(optionGroup, partySize);
  const unmetRequirementsText = getUnmetRequirementsButtonText(optionGroup, numSelected, partySize);
  const continueText = defaultContinueText;
  return {
    text:
      numSelected >= getMinQuantity(optionGroup, partySize)
        ? continueText
        : numSelected === 0
          ? noneSelectedText
          : unmetRequirementsText,
    disabled:
      isOptionGroupInvalid ||
      (disableWhenNoneSelected && numSelected < 1) ||
      numSelected < getMinQuantity(optionGroup, partySize),
  };
}

export function getIsCompactView({
  ticketType,
  isFeatureEnabled,
}: {
  ticketType?: core.ITicketType;
  isFeatureEnabled?: (feature: core.Feature.FeatureName) => boolean;
}): boolean {
  return !!(
    !!ticketType?.addonsCompactView ||
    isOrderVariety(ticketType?.variety) ||
    (isFeatureEnabled &&
      isFeatureEnabled('COMPACT_ADDONS_FOR_ALL') &&
      ticketType?.variety === 'FULL')
  );
}
